import React, { useEffect, useRef, useState } from 'react';
import { Shield, Home, FileText, Share2, LogOut, Download, ChevronDown, User, Lock, Settings, Scale, Menu, X } from 'lucide-react';

/** Purpose: Executes { current view, set current view, permissions = [], audit logs = [], on logout, on change password, on update profile, on go to account, on open legal }. Inputs: { currentView, setCurrentView, permissions = [], auditLogs = [], onLogout, onChangePassword, onUpdateProfile, onGoToAccount, onOpenLegal }. Output: Operation result. */
export default function Header({ currentView, setCurrentView, permissions = [], auditLogs = [], onLogout, onChangePassword, onUpdateProfile, onGoToAccount, onOpenLegal }) {
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const desktopAccountRef = useRef(null);
  const mobileAccountRef = useRef(null);
  const activePermissionsCount = permissions.filter((permission) => permission.status === 'active').length;
  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'records', label: 'Records', icon: FileText },
    { id: 'import', label: 'Import', icon: Download },
    { id: 'providers', label: 'Providers', icon: Share2 },
    { id: 'permissions', label: 'Permissions', icon: Lock, badge: activePermissionsCount > 0 ? activePermissionsCount : null }
  ];
  const accountItems = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      onClick: onGoToAccount || onUpdateProfile,
      disabled: !onGoToAccount && !onUpdateProfile
    },
    {
      id: 'security',
      label: 'Security',
      icon: Settings,
      onClick: onChangePassword || onGoToAccount,
      disabled: !onChangePassword && !onGoToAccount
    },
    { id: 'activity', label: 'Activity', icon: FileText, onClick: () => setCurrentView('audit') },
    { id: 'legal', label: 'Legal notices', icon: Scale, onClick: onOpenLegal, disabled: !onOpenLegal },
    { id: 'logout', label: 'Sign out', icon: LogOut, onClick: onLogout, destructive: true, disabled: !onLogout }
  ];

  useEffect(() => {
    if (!accountOpen) return;
    /** Purpose: Handles click. Inputs: event. Output: Operation result. */
    const handleClick = (event) => {
      const clickedDesktopAccount = desktopAccountRef.current?.contains(event.target);
      const clickedMobileAccount = mobileAccountRef.current?.contains(event.target);
      if (clickedDesktopAccount || clickedMobileAccount) return;
      setAccountOpen(false);
    };
    /** Purpose: Handles escape. Inputs: event. Output: Operation result. */
    const handleEscape = (event) => {
      if (event.key === 'Escape') setAccountOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [accountOpen]);

  /** Purpose: Routes to a primary view and closes temporary menus. Inputs: view id. Output: none. */
  const handleNavigate = (viewId) => {
    setCurrentView(viewId);
    setMobileMenuOpen(false);
    setAccountOpen(false);
  };

  return (
    <header className="glass border-b border-white/20 sticky top-0 z-50 shadow-glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 cursor-pointer group min-w-0" onClick={() => handleNavigate('home')}>
            <div className="animated-gradient p-2.5 rounded-xl shadow-lg group-hover:shadow-glow-blue transition-shadow">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-brand-600 via-violet-600 to-brand-500 bg-clip-text text-transparent tracking-tight truncate">
                MedCaddy
              </h1>
              <p className="hidden sm:block text-[10px] text-slate-400 font-medium uppercase tracking-widest">Secure Records</p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <nav className="flex items-center bg-slate-100/80 rounded-xl p-1 gap-0.5">
              {navItems.map(({ id, label, icon: Icon, badge }) => (
                <button
                  key={id}
                  onClick={() => handleNavigate(id)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 relative ${
                    currentView === id
                      ? 'bg-white text-slate-900 shadow-card'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${currentView === id ? 'text-brand-600' : ''}`} />
                  <span className="hidden xl:inline">{label}</span>
                  {badge && (
                    <span className={`text-[10px] font-semibold min-w-[18px] h-[18px] flex items-center justify-center rounded-full ${
                      currentView === id
                        ? 'bg-brand-100 text-brand-700'
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {badge > 999 ? '999+' : badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <div className="w-px h-8 bg-slate-200/60 mx-1" />

            <div className="relative" ref={desktopAccountRef}>
              <button
                onClick={() => setAccountOpen((prev) => !prev)}
                className={`p-2.5 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 ${
                  accountOpen
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100/60'
                }`}
                title="Account"
                type="button"
              >
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-white" />
                </div>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${accountOpen ? 'rotate-180' : ''}`} />
              </button>

              {accountOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200/80 rounded-2xl shadow-elevated z-50 overflow-hidden animate-scale-in">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Account</p>
                  </div>
                  <div className="py-1.5 px-1.5">
                    {accountItems.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          disabled={item.disabled}
                          onClick={() => {
                            if (item.disabled) return;
                            setAccountOpen(false);
                            setMobileMenuOpen(false);
                            if (item.onClick) item.onClick();
                          }}
                          className={`w-full px-3 py-2.5 rounded-xl text-left text-sm flex items-center gap-3 transition-colors ${
                            item.disabled
                              ? 'text-slate-300 cursor-not-allowed'
                              : item.destructive
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <div className="relative" ref={mobileAccountRef}>
              <button
                onClick={() => setAccountOpen((prev) => !prev)}
                className={`p-2.5 rounded-xl font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  accountOpen
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100/60'
                }`}
                title="Account"
                type="button"
              >
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-white" />
                </div>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${accountOpen ? 'rotate-180' : ''}`} />
              </button>

              {accountOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200/80 rounded-2xl shadow-elevated z-50 overflow-hidden animate-scale-in">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Account</p>
                  </div>
                  <div className="py-1.5 px-1.5">
                    {accountItems.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          disabled={item.disabled}
                          onClick={() => {
                            if (item.disabled) return;
                            setAccountOpen(false);
                            setMobileMenuOpen(false);
                            if (item.onClick) item.onClick();
                          }}
                          className={`w-full px-3 py-2.5 rounded-xl text-left text-sm flex items-center gap-3 transition-colors ${
                            item.disabled
                              ? 'text-slate-300 cursor-not-allowed'
                              : item.destructive
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className={`p-2.5 rounded-xl transition-colors ${
                mobileMenuOpen ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-100/70'
              }`}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="mt-3 rounded-2xl border border-slate-200/70 bg-white/80 backdrop-blur px-2 py-2 animate-slide-down md:hidden">
            <nav className="grid grid-cols-2 gap-2">
              {navItems.map(({ id, label, icon: Icon, badge }) => (
                <button
                  key={id}
                  onClick={() => handleNavigate(id)}
                  className={`w-full px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 ${
                    currentView === id
                      ? 'bg-brand-50 text-brand-700 border border-brand-200'
                      : 'bg-slate-50 text-slate-600 border border-transparent'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {label}
                  </span>
                  {badge && (
                    <span className="text-[10px] font-semibold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-brand-100 text-brand-700">
                      {badge > 999 ? '999+' : badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
